import React from 'react';
import { Link } from 'react-router-dom';
import { ClipboardList, AlertCircle, CheckCircle } from 'lucide-react';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { ROUTES } from '../../utils/constants';

interface SkillsAssessmentWidgetProps {
  lastAssessmentDate?: Date | string;
}

/** Assessment is considered overdue after 30 days */
const OVERDUE_DAYS = 30;

export const SkillsAssessmentWidget: React.FC<SkillsAssessmentWidgetProps> = ({ lastAssessmentDate }) => {
  const assessmentDate = lastAssessmentDate ? new Date(lastAssessmentDate) : null;
  const neverTaken = !assessmentDate;

  // Calculate whether the assessment is overdue
  let isOverdue = false;
  let daysUntilDue = 0;
  if (assessmentDate) {
    const nextDue = new Date(assessmentDate);
    nextDue.setDate(nextDue.getDate() + OVERDUE_DAYS);
    isOverdue = new Date() > nextDue;
    daysUntilDue = Math.max(
      0,
      Math.ceil((nextDue.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    );
  }

  const showAlert = neverTaken || isOverdue;

  const formatDate = (date: Date) =>
    new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }).format(date);

  return (
    <Card className={showAlert ? 'border-red-300 bg-red-50' : ''}>
      <div className="flex flex-col gap-6">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <h3 className="text-lg font-semibold text-neutral-800 flex items-center gap-2">
              <ClipboardList className={`w-5 h-5 ${showAlert ? 'text-red-600' : 'text-primary'}`} />
              Skills Assessment
              {showAlert && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-bold rounded-full bg-red-600 text-white">
                  {neverTaken ? 'New' : 'Due'}
                </span>
              )}
            </h3>
            <p className="text-sm text-neutral-600">
              {assessmentDate
                ? `Last taken: ${formatDate(assessmentDate)}`
                : 'No assessment taken yet'}
            </p>
          </div>
          {showAlert ? (
            <div className="bg-red-100 p-2 rounded-full">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
          ) : (
            <div className="bg-green-100 p-2 rounded-full">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
          )}
        </div>

        {showAlert ? (
          <div className="flex flex-col gap-4">
            <p className="text-sm text-neutral-700 leading-relaxed">
              {neverTaken
                ? 'Take your first skills assessment to get a personalised AI learning plan.'
                : 'Your skills assessment is overdue. Retake it to keep your learning plan aligned with your progress.'}
            </p>
            <Link to={ROUTES.skillsAssessment} className="w-full">
              <Button
                variant="primary"
                className="w-full bg-red-600 hover:bg-red-700 border-red-600 hover:border-red-700"
              >
                {neverTaken ? 'Take Assessment' : 'Retake Assessment'}
              </Button>
            </Link>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <p className="text-sm text-neutral-600 leading-relaxed">
              Your skills profile is up to date. Next assessment recommended in{' '}
              <span className="font-medium">{daysUntilDue} day{daysUntilDue !== 1 ? 's' : ''}</span>.
            </p>
            <Link to={ROUTES.skillsAssessment} className="w-full">
              <Button variant="outline" className="w-full">
                View Results
              </Button>
            </Link>
          </div>
        )}
      </div>
    </Card>
  );
};
